import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import { type CSSProperties, type FC, type ReactElement, type ReactNode, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import { useDataAttributes } from "../../hooks.ts";
import { Card, type CardProps } from "../Card/Card.tsx";
import { Icon } from "../Icon/Icon.tsx";
import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import { TriggerButton } from "../TriggerButton/TriggerButton.tsx";
import { NotificationIntent } from "./NotificationIntent.tsx";
import style from "./style/Notification.module.css";

export interface NotificationProps extends Pick<CardProps, "area"> {
  title: string;
  subtitle?: string | ReactElement;
  currentToastId?: number | string;
  intent?: NotificationIntent;
  icon?: ReactElement;
  actions?: ReactNode[];
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  noChildrenStyling?: boolean;
}

export const Notification: FC<NotificationProps> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const icon_name = getNotificationIntentIcon(props.intent);

  const dataAttributes = useDataAttributes(props);

  useEffect(() => {
    if (ref.current) ref.current.dataset.testid = "notification-card";
  }, []);

  return (
    <Card
      title={props.title}
      subtitle={props.subtitle}
      icon={
        props.icon ? (
          <Icon icon={props.icon} className={style.icon} />
        ) : (
          <SymbolIcon icon={icon_name} className={style.icon} />
        )
      }
      className={clsx(style.notification, props.className, {
        [style.as_overlay]: !!props.currentToastId,
        [style.info]: props.intent === NotificationIntent.Info || props.intent == null,
        [style.success]: props.intent === NotificationIntent.Success,
        [style.warning]: props.intent === NotificationIntent.Warning,
        [style.error]: props.intent === NotificationIntent.Error,
      })}
      headerClassName={clsx({
        [style.header_info]: props.intent === NotificationIntent.Info || props.intent == null,
        [style.header_success]: props.intent === NotificationIntent.Success,
        [style.header_warning]: props.intent === NotificationIntent.Warning,
        [style.header_error]: props.intent === NotificationIntent.Error,
      })}
      actions={[
        props.actions ??
          (props.currentToastId ? (
            <TriggerButton
              icon={"close"}
              label={"Dismiss"}
              onPress={() => {
                toast.dismiss(props.currentToastId);
              }}
              key={"dismiss"}
            />
          ) : undefined),
      ]}
      noPadding={!props.children}
      ref={ref}
      style={props.style}
      {...dataAttributes}
    >
      {props.noChildrenStyling
        ? props.children
        : props.children && <div style={{ fontSize: 13, overflowWrap: "break-word" }}>{props.children}</div>}
    </Card>
  );
};

function getNotificationIntentIcon(intent?: NotificationIntent): MaterialSymbol {
  let icon_name: MaterialSymbol = "lightbulb";

  if (intent === NotificationIntent.Success) {
    icon_name = "celebration";
  } else if (intent === NotificationIntent.Warning) {
    icon_name = "thunderstorm";
  } else if (intent === NotificationIntent.Error) {
    icon_name = "notification_important";
  }

  return icon_name;
}
